
import React, { useState, useCallback, useMemo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Panel,
  Connection,
  Edge,
  Node,
  NodeTypes
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { LogicNode } from './types';
import LogicSidebar from './LogicSidebar';
import { useToast } from '@/components/ui/use-toast';
import { useLogicFlows } from '@/hooks/useLogicFlows';
import StartNode from './nodes/StartNode';
import ConditionNode from './nodes/ConditionNode';
import ActionNode from './nodes/ActionNode';
import EndNode from './nodes/EndNode';
import NodePropertiesPanel from './NodePropertiesPanel';

const initialNodes: Node[] = [];
const initialEdges: Edge[] = [];

export default function LogicFlowEditor() {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState<LogicNode | null>(null);
  const { toast } = useToast();
  const { saveFlow, loadFlows } = useLogicFlows();

  const nodeTypes = useMemo(() => ({
    start: StartNode,
    condition: ConditionNode,
    action: ActionNode,
    end: EndNode
  }), []);

  const onConnect = useCallback((params: Connection) => {
    setEdges((eds) => addEdge(params, eds));
  }, [setEdges]);

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    setSelectedNode(node as LogicNode);
  }, []);

  const handleSaveFlow = useCallback(() => {
    const flowName = prompt("Enter a name for this flow:");
    if (flowName) {
      saveFlow({
        name: flowName,
        nodes,
        edges
      });
      toast({
        title: "Flow saved",
        description: `"${flowName}" has been saved successfully.`
      });
    }
  }, [nodes, edges, saveFlow, toast]);

  const updateNodeData = useCallback((nodeId: string, newData: any) => {
    setNodes(nds => 
      nds.map(node => {
        if (node.id === nodeId) {
          return { ...node, data: { ...node.data, ...newData } };
        }
        return node;
      })
    );
    setSelectedNode(null);
  }, [setNodes]);

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      
      if (typeof type === 'undefined' || !type) {
        return;
      }

      const position = {
        x: event.clientX - event.currentTarget.getBoundingClientRect().left,
        y: event.clientY - event.currentTarget.getBoundingClientRect().top,
      };

      // Create different data structures based on node type
      let nodeData: any = { label: `${type.charAt(0).toUpperCase() + type.slice(1)}`, type };
      
      if (type === 'condition') {
        nodeData = {
          ...nodeData,
          condition: {
            type: 'manual',
            parameter: '',
            value: null
          }
        };
      } else if (type === 'action') {
        nodeData = {
          ...nodeData,
          action: {
            type: 'calibration',
            system: '',
            documentation: '',
            notes: ''
          }
        };
      }

      const newNode: LogicNode = {
        id: `node_${nodes.length + 1}`,
        type,
        position,
        data: nodeData,
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [nodes, setNodes]
  );

  return (
    <div className="flex h-full">
      <LogicSidebar onSave={handleSaveFlow} />
      <div className="flex-grow h-full relative" onDrop={onDrop} onDragOver={onDragOver}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
        >
          <Background />
          <Controls />
          <MiniMap />
          {selectedNode && (
            <Panel position="top-right" className="bg-background border rounded-md shadow-md p-3 w-80">
              <NodePropertiesPanel 
                node={selectedNode} 
                onClose={() => setSelectedNode(null)}
                onUpdate={updateNodeData}
              />
            </Panel>
          )}
        </ReactFlow>
      </div>
    </div>
  );
}
